import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type FeedbackCategory = 'bug' | 'duvida' | 'melhoria' | 'elogio' | 'cobranca' | 'outro';
export type FeedbackStatus = 'novo' | 'lido' | 'em_analise' | 'resolvido';

export interface Feedback {
  id: string;
  user_id: string;
  subject: string;
  category: FeedbackCategory;
  message: string;
  status: FeedbackStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined from profiles (admin view only)
  profile?: { full_name: string | null; email: string | null };
}

export const FEEDBACK_CATEGORIES: { value: FeedbackCategory; label: string; icon: string }[] = [
  { value: 'bug',       label: 'Bug / Erro',         icon: 'ri-bug-line' },
  { value: 'duvida',    label: 'Dúvida',             icon: 'ri-question-line' },
  { value: 'melhoria',  label: 'Sugestão de melhoria', icon: 'ri-lightbulb-line' },
  { value: 'elogio',    label: 'Elogio',             icon: 'ri-heart-line' },
  { value: 'cobranca',  label: 'Cobrança / Pagamento', icon: 'ri-bank-card-line' },
  { value: 'outro',     label: 'Outro',              icon: 'ri-chat-1-line' },
];

export const FEEDBACK_STATUS: { value: FeedbackStatus; label: string; cls: string }[] = [
  { value: 'novo',       label: 'Novo',        cls: 'bg-sky-100 text-sky-700' },
  { value: 'lido',       label: 'Lido',        cls: 'bg-gray-100 text-gray-600' },
  { value: 'em_analise', label: 'Em análise',  cls: 'bg-amber-100 text-amber-700' },
  { value: 'resolvido',  label: 'Resolvido',   cls: 'bg-emerald-100 text-emerald-700' },
];

export const useFeedback = () => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const prevUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevUserIdRef.current !== user?.id) {
      if (prevUserIdRef.current !== undefined || !user?.id) setFeedbacks([]);
      prevUserIdRef.current = user?.id;
    }
  }, [user?.id]);

  const fetchFeedbacks = useCallback(async () => {
    if (!user) { setFeedbacks([]); setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('feedbacks')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setFeedbacks((data as Feedback[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);

  const submitFeedback = async (values: {
    subject: string;
    category: FeedbackCategory;
    message: string;
  }): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Não autenticado.' };
    const { error } = await supabase.from('feedbacks').insert({
      user_id: user.id,
      ...values,
      status: 'novo',
    });
    if (error) return { success: false, error: error.message };
    await fetchFeedbacks();
    return { success: true };
  };

  return { feedbacks, loading, submitFeedback, refetch: fetchFeedbacks };
};

// ── Admin hook ──────────────────────────────────────────────────────────────
export const useAdminFeedbacks = () => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('feedbacks')
      .select('*')
      .order('created_at', { ascending: false });
    setFeedbacks((data as Feedback[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async (id: string, status: FeedbackStatus, adminNotes?: string) => {
    const { error } = await supabase
      .from('feedbacks')
      .update({ status, admin_notes: adminNotes ?? null, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) await fetchAll();
    return { error: error?.message ?? null };
  };

  return { feedbacks, loading, updateStatus, refetch: fetchAll };
};
